import React, { useCallback, useEffect, useRef, useState } from "react";

import { differenceBy, find, first, reject, sortBy, uniqBy } from "lodash/fp";

import type { TagColor } from "@app/analytics/components/Tag/utils";
import {
  getSuggestedTags,
  isNewTag,
} from "@app/analytics/components/TagPopover/utils";
import type { Tag } from "@app/common/types";
import { replaceWhere } from "@app/utils/arrays";

export interface UseManageTagsArgs {
  /** All tags currently available for the user */
  allTags: Tag[];
  /** All tags currently applied to this entity */
  appliedTags: Tag[];
  canApplyToSeries: boolean;
  onSave: (tags: Tag[], applyToSeries?: boolean) => void;
}

export interface UseManageTagsReturn {
  /** The tags that are currently applied to this entity (includes tags that have been applied but not saved) */
  appliedTags: Tag[];
  applyToSeries: boolean;
  /** The tags that are available to be applied to this entity (i.e., are not already applied) */
  availableTags: Tag[];
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  inputValue: string;
  suggestedTags: Tag[];

  focusInput: () => void;
  onInputChange: React.ChangeEventHandler<HTMLInputElement>;
  onInputKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  onRemoveAll: () => void;
  onRemoveTag: (tag: Tag) => () => void;
  onSave: () => void;
  onSelectExistingTag: (tag: Tag) => void;
  onSelectSuggestedTag: (tag: Tag) => void;
  onTagColorChange: (tagId: Tag["id"]) => (color: TagColor) => void;
  onToggleApplyToSeries: (
    event: React.SyntheticEvent,
    checked: boolean
  ) => void;
  reset: () => void;
}

const useManageTags = ({
  allTags,
  appliedTags,
  canApplyToSeries,
  onSave,
}: UseManageTagsArgs): UseManageTagsReturn => {
  const [inputValue, setInputValue] = useState("");
  const [suggestedTags, setSuggestedTags] = useState<Tag[]>([]);

  // intermediate "allTags" state so that changes only persist on save
  const [unsavedAllTags, setUnsavedAllTags] = useState<Tag[]>(allTags);
  // intermediate "appliedTags" state so that changes only persist on save
  const [unsavedAppliedTags, setUnsavedAppliedTags] =
    useState<Tag[]>(appliedTags);
  const [applyToSeries, setApplyToSeries] = useState(true);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const unappliedTags = sortBy(
    "name",
    differenceBy("id", unsavedAllTags, unsavedAppliedTags)
  );

  useEffect(() => {
    resetInitialState();
  }, [allTags.length, appliedTags.length]);

  const focusInput = () => inputRef.current?.click();

  const clearInputValue = useCallback(() => {
    setInputValue("");
    setSuggestedTags([]);
  }, []);

  const resetInitialState = () => {
    setUnsavedAppliedTags(appliedTags);
    setUnsavedAllTags(allTags);
    setApplyToSeries(true);
    clearInputValue();
  };

  const handleToggleApplyToSeries = useCallback(
    (_e: React.SyntheticEvent, checked: boolean) => {
      setApplyToSeries(checked);
    },
    []
  );

  const handleRemoveAll = useCallback(() => {
    setUnsavedAppliedTags([]);
  }, []);

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    const inputValue = event.target.value;
    const suggestedTag = getSuggestedTags(
      inputValue,
      unsavedAllTags,
      suggestedTags,
      unsavedAppliedTags
    );
    setInputValue(inputValue);
    setSuggestedTags(suggestedTag);
  };

  const handleTagColorChange = (tagId: Tag["id"]) => (color: TagColor) => {
    setSuggestedTags((tags) => {
      const tagToUpdate = find({ id: tagId }, tags);
      if (!tagToUpdate) return tags;

      const updatedTag: Tag = { ...tagToUpdate, color: color.backgroundColor };
      return replaceWhere<Tag>(tags, updatedTag, ({ id }) => id === tagId);
    });
  };

  const handleSelectSuggestedTag = (tag: Tag) => {
    if (isNewTag(tag)) {
      setUnsavedAllTags((tags) => uniqBy("id", [...tags, tag]));
    }
    setUnsavedAppliedTags((tags) => uniqBy("id", [...tags, tag]));
    clearInputValue();
    focusInput();
  };

  const handleSelectExistingTag = (tag: Tag) => {
    setUnsavedAppliedTags((tags) => uniqBy("id", [...tags, tag]));
    focusInput();
  };

  const handleRemoveTag = (tag: Tag) => () => {
    setUnsavedAppliedTags((tags) => reject({ id: tag.id }, tags));
  };

  const handleInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    event
  ) => {
    switch (event.key) {
      case "Escape":
        event.stopPropagation();
        event.preventDefault();
        clearInputValue();
        break;
      case "Enter": {
        event.stopPropagation();
        event.preventDefault();
        const suggestedTag = first(suggestedTags);
        if (suggestedTag) {
          handleSelectSuggestedTag(suggestedTag);
        }
        break;
      }
      default:
        break;
    }
  };

  const handleSave = () => {
    onSave(unsavedAppliedTags, canApplyToSeries ? applyToSeries : undefined);
  };

  return {
    appliedTags: unsavedAppliedTags,
    applyToSeries,
    availableTags: unappliedTags,
    inputRef,
    inputValue,
    suggestedTags,

    focusInput,
    onInputChange: handleInputChange,
    onInputKeyDown: handleInputKeyDown,
    onRemoveAll: handleRemoveAll,
    onRemoveTag: handleRemoveTag,
    onSave: handleSave,
    onSelectExistingTag: handleSelectExistingTag,
    onSelectSuggestedTag: handleSelectSuggestedTag,
    onTagColorChange: handleTagColorChange,
    onToggleApplyToSeries: handleToggleApplyToSeries,
    reset: resetInitialState,
  };
};

export default useManageTags;
